import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Users, BookOpen, MessageSquare, Shield, LogOut,
  Search, Lightbulb, Target, User, Menu, X, Calendar
} from 'lucide-react';
import { useState } from 'react';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Community', path: '/community', icon: Users },
  { label: 'Power Vault', path: '/power-vault', icon: BookOpen },
  { label: 'AI Guide', path: '/ai-guide', icon: MessageSquare },
  { label: 'Pivot Journey', path: '/lifestyle-designer', icon: Target },
  { label: 'Events', path: '/events', icon: Calendar },
  { label: 'Power Lab', path: '/idea-lab', icon: Lightbulb },
];

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');
  const handleLogout = () => { localStorage.clear(); navigate('/'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A0533] via-[#2D074E] to-[#1A0533] text-white font-inter">
      <nav className="sticky top-0 z-50 bg-[#1A0533]/70 backdrop-blur-2xl border-b border-white/[0.06]">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-2.5 flex items-center justify-between">
          <motion.div className="flex items-center space-x-3 cursor-pointer" whileHover={{ scale: 1.02 }}
            onClick={() => navigate('/dashboard')}>
            <img src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png"
              alt="P2P" className="w-8 h-8 rounded-full border border-white/20" />
            <span className="text-lg font-cormorant font-bold text-white tracking-tight hidden sm:inline">Pivot To Power</span>
          </motion.div>
          <div className="hidden lg:flex items-center space-x-1">
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <motion.button key={item.path} onClick={() => navigate(item.path)}
                  whileHover={{ y: -1 }} whileTap={{ scale: 0.97 }}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-inter transition-all duration-200 ${
                    active ? 'bg-white/10 text-white font-semibold' : 'text-[#A39BB0] hover:text-white hover:bg-white/5'
                  }`} data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /><span>{item.label}</span>
                </motion.button>
              );
            })}
          </div>
          <div className="flex items-center space-x-3">
            {user.role === 'admin' && (
              <motion.button onClick={() => navigate('/admin')} whileHover={{ y: -1 }}
                className="text-[#D4AF37] hover:text-amber-300 font-inter text-xs transition-all flex items-center space-x-1 hidden md:flex"
                data-testid="nav-admin">
                <Shield className="w-3.5 h-3.5" /><span>Admin</span>
              </motion.button>
            )}
            <motion.button onClick={() => navigate(`/profile/${user.user_id}`)} whileHover={{ scale: 1.1 }}
              className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
              data-testid="nav-profile">
              {user.full_name?.charAt(0) || '?'}
            </motion.button>
            <motion.button onClick={handleLogout} whileHover={{ scale: 1.1 }}
              className="text-[#A39BB0] hover:text-white transition-colors hidden md:block" data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </motion.button>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden text-white">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            className="lg:hidden border-t border-white/10 bg-[#1A0533]/95 backdrop-blur-xl px-4 py-2 overflow-hidden">
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <button key={item.path} onClick={() => { navigate(item.path); setMobileOpen(false); }}
                  className={`flex items-center space-x-2 w-full py-2.5 text-sm font-inter ${active ? 'text-white font-semibold' : 'text-[#A39BB0]'}`}>
                  <Icon className="w-4 h-4" /><span>{item.label}</span>
                </button>
              );
            })}
            <button onClick={handleLogout} className="flex items-center space-x-2 w-full py-2.5 text-sm font-inter text-[#A39BB0]">
              <LogOut className="w-4 h-4" /><span>Logout</span>
            </button>
          </motion.div>
        )}
      </nav>
      <motion.main initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="relative z-10">
        {children}
      </motion.main>
    </div>
  );
}
